import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import Table from 'Components/Table';
import useStickyTableHead from 'Hooks/useStickyTableHead';
import { OnClickEvent, KeyEvent } from 'Utils';
import useGetListTableColumns from './useGetListTableColumns';
import DeleteListModal from '../DeleteListModal';

import styles from './ListTable.css';

type Props = {
    data: ListApiGetResponse;
};

const TABLE_ID = 'LIST-MANAGEMENT-TABLE';

function ListTable({ data }: Props) {
    const history = useHistory();
    const { path } = useRouteMatch();
    const [deleteListId, setDeleteListId] = React.useState<undefined | number>(undefined);
    const deleteListName = data.result.find((l) => l.id === deleteListId)?.name;

    const onClose = () => {
        setDeleteListId(undefined);
    };
    const handleDelete = <T,>(id?: number, event?: OnClickEvent<T> | KeyEvent<T>) => {
        if (id) {
            event?.stopPropagation();
            setDeleteListId(id);
        }
    };
    const columns = useGetListTableColumns(handleDelete);
    useStickyTableHead(TABLE_ID);

    return (
        <>
            <div className={styles.listTable}>
                <Table
                    tableName={TABLE_ID}
                    dataTestId={TABLE_ID}
                    showExpandableSearch
                    showRichFilter
                    showMultiSort
                    clickableRow
                    enablePagination
                    initialState={{
                        pageSize: 20,
                        sortBy: [{ id: '_formatted.modifiedAt', desc: true }]
                    }}
                    noDataMessage='No lists found. Click the Add List button to create a new list.'
                    columns={columns}
                    data={data.result}
                    onRowInteractCB={(row, e) => {
                        /** not the most graceful handling but basically
                         * check to see if the element clicked on is "inside" the same dom tree
                         * popper will be outside this tree (we don't want to route if they click a popper element)
                         * we also want to ignore if the element explicitly says "ignore me"
                         */
                        const targetAsAny = e.target as any; // do this so that we can do things like contains
                        if (
                            e.currentTarget.contains(targetAsAny) &&
                            // hack to see if the target is something we can interact with
                            !targetAsAny?.getAttribute?.('data-ignore-on-row-interact')
                        ) {
                            trackEvent({
                                eventType: EventTypes.track,
                                eventName: EventNames.click,
                                payload: {
                                    clickIdentifier: 'Lists - View List',
                                    text: 'View List'
                                }
                            });
                            history.push(`${path}/${row?.original.id}`);
                        }
                    }}
                />
            </div>
            <DeleteListModal listId={deleteListId} onClose={onClose} listName={deleteListName} />
        </>
    );
}

export default ListTable;
